(function() {
    'use strict';
    
    // Initialize dataLayer if it doesn't exist
    window.dataLayer = window.dataLayer || [];
    
    // Configuration
    const config = {
        debug: true, // Set to false in production
        trackViews: true,
        trackClicks: true,
        trackGeolocation: true,
        viewThreshold: 100, // Time in ms before counting as a view (1.5 seconds)
        maxInitRetries: 20, // Maximum number of times to check for galleries
        initRetryDelay: 500, // Delay between checks in ms
        
        // XO Gallery specific selectors
        gallerySelectors: [
            '.xo-gallery.xo-gallery-id-11',  // Announcement bar gallery
            '.xo-gallery.xo-gallery-id-1',   // Sidebar gallery
            '.xo-gallery.xo-gallery-id-3'    // Product loop gallery
        ],
        
        // Gallery names for reporting
        galleryNames: {
            'xo-gallery-id-11': 'Announcement Bar Gallery',
            'xo-gallery-id-1': 'Sidebar Gallery',
            'xo-gallery-id-3': 'Product Loop Gallery'
        }
    };
    
    // Storage for tracking
    const trackingData = {
        viewedImages: new Set(),
        clickedImages: new Set(),
        geolocation: null,
        sessionId: generateSessionId(),
        pageLoadTime: new Date().toISOString(),
        initRetryCount: 0,
        isInitialized: false
    };
    
    /**
     * Generate unique session ID
     */
    function generateSessionId() {
        return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    }
    
    /**
     * Debug logging
     */
    function log(...args) {
        if (config.debug) {
            console.log('[XO Gallery Tracking]', ...args);
        }
    }
    
    /**
     * Get geolocation data from IP
     */
    async function getGeolocation() {
        if (trackingData.geolocation) {
            return trackingData.geolocation;
        }
        
        try {
            const response = await fetch('https://ipapi.co/json/', {
                method: 'GET',
                headers: { 'Accept': 'application/json' }
            });
            
            if (!response.ok) {
                throw new Error('Geolocation API request failed');
            }
            
            const data = await response.json();
            
            trackingData.geolocation = {
                country: data.country_name || 'Unknown',
                countryCode: data.country_code || 'Unknown',
                region: data.region || 'Unknown',
                city: data.city || 'Unknown',
                postal: data.postal || 'Unknown',
                latitude: data.latitude || null,
                longitude: data.longitude || null,
                timezone: data.timezone || 'Unknown',
                ip: data.ip || 'Unknown'
            };
            
            log('Geolocation obtained:', trackingData.geolocation);
            return trackingData.geolocation;
            
        } catch (error) {
            log('Geolocation error:', error);
            trackingData.geolocation = {
                country: 'Unknown',
                countryCode: 'Unknown',
                region: 'Unknown',
                city: 'Unknown',
                postal: 'Unknown',
                latitude: null,
                longitude: null,
                timezone: 'Unknown',
                ip: 'Unknown',
                error: error.message
            };
            return trackingData.geolocation;
        }
    }
    
    /**
     * Extract gallery ID from element classes
     */
    function getGalleryId(element) {
        const classList = element.className || '';
        const match = classList.match(/xo-gallery-id-(\d+)/);
        return match ? `xo-gallery-id-${match[1]}` : 'unknown';
    }
    
    /**
     * Get gallery name from ID
     */
    function getGalleryName(galleryId) {
        return config.galleryNames[galleryId] || galleryId;
    }
    
    /**
     * Extract image details from XO Gallery element
     */
    function getImageDetails(imgElement, galleryElement) {
        const src = imgElement.src || imgElement.dataset.src || '';
        const alt = imgElement.alt || '';
        
        // Extract gallery information
        const galleryId = getGalleryId(galleryElement);
        const galleryName = getGalleryName(galleryId);
        
        // Get the link element (parent anchor tag)
        const linkElement = imgElement.closest('a');
        const externalUrl = linkElement ? linkElement.href : '';
        
        // Extract image filename for unique identification
        const urlParts = src.split('/');
        const filenameWithParams = urlParts[urlParts.length - 1];
        const filename = filenameWithParams.split('?')[0];
        
        // Create unique image identifier
        const imageId = `${galleryId}_${filename}`;
        
        // Get image dimensions
        const imageWidth = imgElement.naturalWidth || imgElement.width || 0;
        const imageHeight = imgElement.naturalHeight || imgElement.height || 0;
        
        // Get position information
        const rect = imgElement.getBoundingClientRect();
        
        return {
            // Image identifiers
            imageId: imageId,
            imageUrl: src,
            imageName: filename,
            imageAlt: alt,
            
            // Gallery information
            galleryId: galleryId,
            galleryName: galleryName,
            
            // Link information
            externalUrl: externalUrl,
            isExternalLink: externalUrl && !externalUrl.includes(window.location.hostname),
            
            // Image properties
            imageWidth: imageWidth,
            imageHeight: imageHeight,
            imageDimensions: `${imageWidth}x${imageHeight}`,
            
            // Position information
            positionTop: Math.round(rect.top),
            positionLeft: Math.round(rect.left),
            
            // Page context
            pageUrl: window.location.href,
            pageTitle: document.title,
            pagePath: window.location.pathname,
            
            // Timestamp
            timestamp: new Date().toISOString(),
            
            // Session
            sessionId: trackingData.sessionId
        };
    }
    
    /**
     * Push event to dataLayer
     */
    async function pushToDataLayer(eventName, eventData) {
        const geo = await getGeolocation();
        
        const dataLayerEvent = {
            event: eventName,
            ...eventData,
            
            // Geolocation data
            geo_country: geo.country,
            geo_countryCode: geo.countryCode,
            geo_region: geo.region,
            geo_city: geo.city,
            geo_postal: geo.postal,
            geo_latitude: geo.latitude,
            geo_longitude: geo.longitude,
            geo_timezone: geo.timezone,
            geo_ip: geo.ip,
            
            // Browser/device information
            userAgent: navigator.userAgent,
            screenResolution: `${window.screen.width}x${window.screen.height}`,
            viewportSize: `${window.innerWidth}x${window.innerHeight}`,
            devicePixelRatio: window.devicePixelRatio || 1,
            
            // Timing
            timeOnPage: Math.round((Date.now() - new Date(trackingData.pageLoadTime).getTime()) / 1000)
        };
        
        window.dataLayer.push(dataLayerEvent);
        log('Event pushed to dataLayer:', eventName, dataLayerEvent);
    }
    
    /**
     * Track image view
     */
    function trackImageView(imgElement, galleryElement) {
        const imageDetails = getImageDetails(imgElement, galleryElement);
        
        if (trackingData.viewedImages.has(imageDetails.imageId)) {
            return; // Already tracked
        }
        
        trackingData.viewedImages.add(imageDetails.imageId);
        
        pushToDataLayer('xo_gallery_image_view', {
            eventCategory: 'XO Gallery',
            eventAction: 'Image View',
            eventLabel: imageDetails.imageName,
            ...imageDetails
        });
        
        log('Image view tracked:', imageDetails.imageName, 'in', imageDetails.galleryName);
    }
    
    /**
     * Track image click
     */
    function trackImageClick(imgElement, galleryElement, clickEvent) {
        const imageDetails = getImageDetails(imgElement, galleryElement);
        
        // Track click (can be tracked multiple times unlike views)
        const clickId = `${imageDetails.imageId}_${Date.now()}`;
        trackingData.clickedImages.add(clickId);
        
        pushToDataLayer('xo_gallery_image_click', {
            eventCategory: 'XO Gallery',
            eventAction: 'Image Click',
            eventLabel: imageDetails.imageName,
            clickX: clickEvent.clientX,
            clickY: clickEvent.clientY,
            clickPageX: clickEvent.pageX,
            clickPageY: clickEvent.pageY,
            ...imageDetails
        });
        
        log('Image click tracked:', imageDetails.imageName, 'in', imageDetails.galleryName);
    }
    
    /**
     * Setup Intersection Observer for view tracking
     */
    function setupViewTracking() {
        if (!config.trackViews) return;
        
        const observerOptions = {
            root: null,
            rootMargin: '0px',
            threshold: 0.5 // 50% of image must be visible
        };
        
        const viewTimers = new Map();
        
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                const img = entry.target;
                const galleryElement = img.closest(config.gallerySelectors.join(','));
                
                if (!galleryElement) return;
                
                if (entry.isIntersecting) {
                    // Start timer when image becomes visible
                    const timer = setTimeout(() => {
                        trackImageView(img, galleryElement);
                    }, config.viewThreshold);
                    
                    viewTimers.set(img, timer);
                } else {
                    // Clear timer if image leaves viewport before threshold
                    const timer = viewTimers.get(img);
                    if (timer) {
                        clearTimeout(timer);
                        viewTimers.delete(img);
                    }
                }
            });
        }, observerOptions);
        
        // Observe all XO Gallery images
        let totalImages = 0;
        config.gallerySelectors.forEach(selector => {
            document.querySelectorAll(`${selector} img`).forEach(img => {
                observer.observe(img);
                totalImages++;
                log('Observing image:', img.src);
            });
        });
        
        log('View tracking initialized for', totalImages, 'images');
        return totalImages;
    }
    
    /**
     * Setup click tracking
     */
    function setupClickTracking() {
        if (!config.trackClicks) return;
        
        document.addEventListener('click', (e) => {
            // Check if click is on an image or link within XO Gallery
            const target = e.target;
            const link = target.closest('a');
            const img = target.tagName === 'IMG' ? target : target.querySelector('img');
            
            if (!img) return;
            
            const galleryElement = img.closest(config.gallerySelectors.join(','));
            
            if (galleryElement) {
                trackImageClick(img, galleryElement, e);
            }
        }, true);
        
        log('Click tracking initialized');
    }
    
    /**
     * Setup mutation observer for dynamically loaded galleries
     */
    function setupMutationObserver() {
        const observer = new MutationObserver((mutations) => {
            let needsReinit = false;
            
            mutations.forEach(mutation => {
                mutation.addedNodes.forEach(node => {
                    if (node.nodeType === 1) { // Element node
                        // Check if the added node is an XO Gallery or contains one
                        const isGallery = config.gallerySelectors.some(selector => 
                            node.matches && node.matches(selector)
                        );
                        
                        const hasGallery = node.querySelectorAll && 
                            config.gallerySelectors.some(selector => 
                                node.querySelectorAll(selector).length > 0
                            );
                        
                        // Check if images were added to existing galleries
                        const hasImages = node.tagName === 'IMG' && 
                            node.closest(config.gallerySelectors.join(','));
                        
                        if (isGallery || hasGallery || hasImages) {
                            needsReinit = true;
                        }
                    }
                });
            });
            
            if (needsReinit) {
                log('New XO Gallery content detected, reinitializing view tracking');
                setupViewTracking();
            }
        });
        
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
        
        log('Mutation observer initialized');
    }
    
    /**
     * Track page view with gallery information
     */
    function trackPageView() {
        const galleries = {};
        
        config.gallerySelectors.forEach(selector => {
            const elements = document.querySelectorAll(selector);
            const galleryId = selector.match(/xo-gallery-id-(\d+)/)?.[0] || selector;
            const galleryName = getGalleryName(galleryId);
            
            if (elements.length > 0) {
                galleries[galleryId] = {
                    name: galleryName,
                    count: elements.length,
                    totalImages: Array.from(elements).reduce((sum, el) => 
                        sum + el.querySelectorAll('img').length, 0
                    )
                };
            }
        });
        
        pushToDataLayer('xo_gallery_page_view', {
            eventCategory: 'XO Gallery',
            eventAction: 'Page View',
            eventLabel: document.title,
            pageUrl: window.location.href,
            pageTitle: document.title,
            pagePath: window.location.pathname,
            galleries: galleries,
            totalGalleries: Object.keys(galleries).length
        });
    }
    
    /**
     * Check if galleries have loaded with images
     */
    function checkGalleriesLoaded() {
        let totalImages = 0;
        
        config.gallerySelectors.forEach(selector => {
            const galleries = document.querySelectorAll(selector);
            galleries.forEach(gallery => {
                const images = gallery.querySelectorAll('img');
                totalImages += images.length;
            });
        });
        
        return totalImages > 0;
    }
    
    /**
     * Initialize tracking (with retry logic for dynamic loading)
     */
    function initializeTracking() {
        log('Attempting to initialize tracking (attempt', trackingData.initRetryCount + 1, 'of', config.maxInitRetries, ')');
        
        // Check if galleries have loaded
        if (checkGalleriesLoaded()) {
            log('XO Galleries detected with images, initializing tracking...');
            
            // Track page view
            trackPageView();
            
            // Setup tracking
            const imageCount = setupViewTracking();
            setupClickTracking();
            setupMutationObserver();
            
            trackingData.isInitialized = true;
            
            log('XO Gallery tracking initialized successfully');
            log('Session ID:', trackingData.sessionId);
            log('Tracking galleries:', config.gallerySelectors);
            log('Total images found:', imageCount);
            
        } else {
            // Galleries not loaded yet, retry
            trackingData.initRetryCount++;
            
            if (trackingData.initRetryCount < config.maxInitRetries) {
                log('XO Galleries not loaded yet, retrying in', config.initRetryDelay, 'ms...');
                setTimeout(initializeTracking, config.initRetryDelay);
            } else {
                log('Max retries reached. XO Galleries may not be present on this page, or took too long to load.');
                log('Setting up mutation observer to catch late-loading galleries...');
                
                // Still set up mutation observer and click tracking in case galleries load later
                setupClickTracking();
                setupMutationObserver();
                trackingData.isInitialized = true;
            }
        }
    }
    
    /**
     * Initialize tracking
     */
    function init() {
        log('Initializing XO Gallery tracking...');
        
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', init);
            return;
        }
        
        // Get geolocation early (async)
        if (config.trackGeolocation) {
            getGeolocation();
        }
        
        // Start initialization with retry logic
        initializeTracking();
    }
    
    // Start initialization
    init();
    
    // Expose API for debugging and manual tracking
    window.xoGalleryTracking = {
        config: config,
        trackingData: trackingData,
        getGeolocation: getGeolocation,
        reinitialize: initializeTracking,
        manualTrackView: (imgElement) => {
            const gallery = imgElement.closest(config.gallerySelectors.join(','));
            if (gallery) trackImageView(imgElement, gallery);
        },
        manualTrackClick: (imgElement, event) => {
            const gallery = imgElement.closest(config.gallerySelectors.join(','));
            if (gallery) trackImageClick(imgElement, gallery, event);
        },
        getStats: () => ({
            viewedImages: trackingData.viewedImages.size,
            clickedImages: trackingData.clickedImages.size,
            sessionId: trackingData.sessionId,
            geolocation: trackingData.geolocation,
            isInitialized: trackingData.isInitialized,
            initRetryCount: trackingData.initRetryCount
        })
    };
    
})();
